import { useMemo, type ReactNode } from 'react'
import { Clock } from 'lucide-react'

import { cn } from 'lib/utils'

/**
 * The "Recent Activity" panel the index screens carry in their rightbar,
 * as one presentational component.
 *
 * The existing per-screen versions (SidebarLocationsRecent, SidebarProjectsRecent,
 * SidebarPortfoliosRecent and friends) each re-implement the same header,
 * timeline rail and empty state around a different record shape. This takes the
 * records already shaped by the host screen instead, so a screen that already
 * holds its data can show a recent list without a new component or a new fetch.
 *
 * Ordering and the cap live here rather than in each caller: a list titled
 * "recent" that is not in recency order is wrong in a way nobody reports,
 * because it still looks like a list.
 */

const DEFAULT_LIMIT = 10

export interface RecentActivityItem {
	id: string
	name: string
	/** Second line: whatever identifies the record at a glance. */
	detail?: string | null
	/** ISO date the item was last touched. Drives ordering and the timestamp. */
	timestamp?: string | null
	/** Thumbnail. Falls back to `icon`, then to the first letter of the name. */
	imageUrl?: string | null
	icon?: ReactNode
}

export interface SidebarRecentActivityProps {
	title?: string
	items: RecentActivityItem[]
	isLoading?: boolean
	emptyMessage?: string
	emptyHint?: string
	/** Omit to render the list as plain, non-interactive rows. */
	onSelect?: (item: RecentActivityItem) => void
	limit?: number
}

function formatActivityDate(dateStr?: string | null): string {
	if (!dateStr) return ''
	const d = new Date(dateStr)
	if (Number.isNaN(d.getTime())) return ''
	const now = new Date()
	const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate())
	const sevenDaysAgo = new Date(todayStart)
	sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7)

	if (d >= todayStart) {
		return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
	}
	if (d >= sevenDaysAgo) {
		return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
	}
	return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

export function SidebarRecentActivity({
	title = 'Recent Activity',
	items,
	isLoading = false,
	emptyMessage = 'No activity yet',
	emptyHint,
	onSelect,
	limit = DEFAULT_LIMIT,
}: SidebarRecentActivityProps) {
	const recents = useMemo(() => {
		const byRecency = [...items].sort((a, b) => {
			const ta = new Date(a?.timestamp || 0).getTime()
			const tb = new Date(b?.timestamp || 0).getTime()
			return tb - ta
		})
		return byRecency.slice(0, limit)
	}, [items, limit])

	// A spinner over a list that is already on screen reads as the list going
	// away, so only an empty panel says it is still loading.
	const showLoading = isLoading && recents.length === 0

	return (
		<div className="flex flex-col h-full">
			<div className="px-4 py-3 border-b border-gray-100">
				<div className="flex items-center gap-2">
					<Clock className="w-4 h-4 text-gray-400" />
					<span className="text-sm font-semibold text-gray-900">{title}</span>
					{recents.length > 0 && (
						<span className="text-xs px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500">
							{recents.length}
						</span>
					)}
				</div>
			</div>

			<div className="flex-1 overflow-y-auto">
				{showLoading ? (
					<div className="flex items-center justify-center h-full p-4">
						<span className="text-sm text-gray-400">Loading...</span>
					</div>
				) : recents.length === 0 ? (
					<div className="text-center py-6 px-4">
						<Clock className="w-5 h-5 mx-auto text-gray-300 mb-2" />
						<p className="text-sm text-gray-400">{emptyMessage}</p>
						{emptyHint && <p className="mt-1 text-xs text-gray-400">{emptyHint}</p>}
					</div>
				) : (
					<div className="flow-root px-4 pt-3">
						<ul className="-mb-6">
							{recents.map((item, idx) => (
								<li key={item.id} className="relative pb-6 last:pb-0">
									{idx !== recents.length - 1 && (
										<span
											className="absolute left-[15px] top-8 -ml-px h-full w-0.5 bg-gray-200"
											aria-hidden="true"
										/>
									)}
									<RecentRow item={item} onSelect={onSelect} />
								</li>
							))}
						</ul>
					</div>
				)}
			</div>
		</div>
	)
}

function RecentRow({
	item,
	onSelect,
}: {
	item: RecentActivityItem
	onSelect?: (item: RecentActivityItem) => void
}) {
	const body = (
		<>
			<span className="h-8 w-8 shrink-0 rounded-full overflow-hidden bg-blue-50 text-blue-600 flex items-center justify-center">
				{item.imageUrl ? (
					<img src={item.imageUrl} alt="" loading="lazy" className="h-8 w-8 object-cover" />
				) : (
					item.icon ?? (
						<span className="text-xs font-semibold">
							{(item.name || '?').charAt(0).toUpperCase()}
						</span>
					)
				)}
			</span>
			<span className="min-w-0 flex-1 pt-0.5">
				<span
					className={cn(
						'block text-sm font-medium text-gray-900 truncate',
						onSelect && 'group-hover:text-[#0080fe] transition-colors',
					)}
					title={item.name}
				>
					{item.name}
				</span>
				{item.detail && (
					<span className="mt-0.5 block text-xs text-gray-500 truncate" title={item.detail}>
						{item.detail}
					</span>
				)}
				{formatActivityDate(item.timestamp) && (
					<span className="mt-0.5 block text-xs text-gray-400">
						{formatActivityDate(item.timestamp)}
					</span>
				)}
			</span>
		</>
	)

	if (!onSelect) {
		return <div className="relative flex items-start gap-3">{body}</div>
	}

	return (
		<button
			type="button"
			onClick={() => onSelect(item)}
			className="relative flex w-full items-start gap-3 text-left group"
		>
			{body}
		</button>
	)
}

export default SidebarRecentActivity
